import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import CardDetailPanel from "@/components/board/CardDetailPanel";
import { stageColor } from "@/lib/palette";
import { deriveInitials } from "@/lib/pipeline";
import { Inbox as InboxIcon, ListChecks, Clock, Ban, AtSign, Moon } from "lucide-react";
import { PASTEL } from "@/lib/pastel";
import { toast } from "sonner";

export default function InboxPage() {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState({});
  const [selected, setSelected] = useState(null);

  const load = () =>
    Promise.all([api.get("/inbox"), api.get("/users"), api.get("/templates"), api.get("/lists")]).then(
      ([i, u, t, l]) => {
        setData(i.data);
        setUsers(u.data);
        setTemplates(t.data);
        setLists(l.data);
      }
    );

  useEffect(() => {
    load();
  }, []);

  const tplByStage = useMemo(
    () => Object.fromEntries(templates.map((t) => [t.stage_number, t])),
    [templates]
  );

  const openCard = async (id) => {
    const { data: c } = await api.get(`/cards/${id}`);
    setSelected(c);
  };

  const markAllRead = async () => {
    await api.post("/mentions/read-all");
    toast.success("Mentions cleared");
    load();
  };

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col paper-bg">
        <Header />
        <div className="px-4 sm:px-6 lg:px-8 py-12 text-sm text-[#15233B]/50">Loading…</div>
      </div>
    );
  }

  const today = data.today;
  const Section = ({ title, icon: Icon, pastel, count, children, testId, action }) => (
    <section
      data-testid={testId}
      className="bg-white rounded-lg shadow-sm overflow-hidden border"
      style={{ borderColor: pastel.border }}
    >
      <div
        className="px-5 py-3 flex items-center gap-2 border-b"
        style={{ background: pastel.tint, borderColor: pastel.border, color: pastel.accent }}
      >
        <Icon className="h-4 w-4" />
        <h3 className="text-sm font-extrabold tracking-tight uppercase tracking-[0.15em]">
          {title}
        </h3>
        <span
          className="ml-2 text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/70"
          style={{ color: pastel.accent }}
        >
          {count}
        </span>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {children}
    </section>
  );

  const Row = ({ card_full_name, card_av_id, card_id, card_stage, stage_name, children }) => {
    const sc = stageColor(card_stage);
    return (
      <li
        onClick={() => openCard(card_id)}
        className="px-5 py-3 text-sm flex items-center gap-3 hover:bg-[#F6F3EC]/60 cursor-pointer"
      >
        <div
          className="h-9 w-9 rounded-full grid place-items-center text-xs font-bold shadow-sm shrink-0"
          style={{ backgroundColor: sc.primary, color: sc.text }}
        >
          {deriveInitials(card_full_name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[#15233B] truncate">
            {card_full_name} ·{" "}
            <span className="font-mono text-xs text-[#15233B]/60">{card_av_id}</span>
          </div>
          <div className="text-xs text-[#15233B]/60 truncate">
            Stage {card_stage}: {stage_name}
          </div>
        </div>
        {children}
      </li>
    );
  };

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-5xl w-full mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <InboxIcon className="h-6 w-6 text-[#FBBF24]" />
          <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">Inbox</h1>
        </div>
        <p className="text-sm text-[#15233B]/60 -mt-2">
          Everything that needs you today — tasks assigned, due actions, blocked cards, mentions.
        </p>

        <Section
          title="Tasks assigned to me"
          icon={ListChecks}
          pastel={PASTEL.green}
          count={data.counts.my_tasks}
          testId="inbox-my-tasks"
        >
          <ul className="divide-y divide-[#15233B]/8">
            {data.my_tasks.length === 0 && (
              <li className="px-5 py-6 text-xs italic text-[#15233B]/40">
                No tasks assigned to you.
              </li>
            )}
            {data.my_tasks.map((t) => {
              const overdue = t.due_date && t.due_date <= today;
              return (
                <Row
                  key={`${t.card_id}-${t.task_index}`}
                  card_full_name={t.card_full_name}
                  card_av_id={t.card_av_id}
                  card_id={t.card_id}
                  card_stage={t.card_stage}
                  stage_name={t.stage_name}
                >
                  <div className="text-right min-w-0">
                    <div className="font-semibold text-[#15233B] truncate max-w-[220px]">
                      {t.title}
                    </div>
                    {t.due_date && (
                      <div
                        className={`text-xs font-bold ${
                          overdue ? "text-[#b91c1c]" : "text-[#15233B]/60"
                        }`}
                      >
                        {t.due_date}
                      </div>
                    )}
                  </div>
                </Row>
              );
            })}
          </ul>
        </Section>

        <Section
          title="Next action due"
          icon={Clock}
          pastel={PASTEL.yellow}
          count={data.counts.next_due}
          testId="inbox-next-due"
        >
          <ul className="divide-y divide-[#15233B]/8">
            {data.next_due.length === 0 && (
              <li className="px-5 py-6 text-xs italic text-[#15233B]/40">Nothing due today.</li>
            )}
            {data.next_due.map((c) => (
              <Row
                key={c.card_id}
                card_full_name={c.card_full_name}
                card_av_id={c.card_av_id}
                card_id={c.card_id}
                card_stage={c.card_stage}
                stage_name={c.stage_name}
              >
                <div className="text-right text-xs text-[#15233B]/70 max-w-[220px]">
                  <div className="font-semibold text-[#15233B]">{c.next_action_date}</div>
                  <div className="italic truncate">{c.next_action_text || "—"}</div>
                </div>
              </Row>
            ))}
          </ul>
        </Section>

        <Section
          title="Blocked (mine)"
          icon={Ban}
          pastel={PASTEL.pink}
          count={data.counts.blocked}
          testId="inbox-blocked"
        >
          <ul className="divide-y divide-[#15233B]/8">
            {data.blocked.length === 0 && (
              <li className="px-5 py-6 text-xs italic text-[#15233B]/40">No blocked cards.</li>
            )}
            {data.blocked.map((c) => (
              <Row
                key={c.card_id}
                card_full_name={c.card_full_name}
                card_av_id={c.card_av_id}
                card_id={c.card_id}
                card_stage={c.card_stage}
                stage_name={c.stage_name}
              >
                <div className="text-xs italic text-[#7e22ce] max-w-[240px] truncate">
                  {c.reason}
                </div>
              </Row>
            ))}
          </ul>
        </Section>

        <Section
          title="Mentions & alerts"
          icon={AtSign}
          pastel={PASTEL.violet}
          count={data.counts.mentions}
          testId="inbox-mentions"
          action={
            data.mentions.some((m) => !m.read) && (
              <button
                onClick={markAllRead}
                data-testid="mentions-mark-all-read"
                className="text-xs font-semibold hover:underline"
                style={{ color: PASTEL.violet.accent }}
              >
                Mark all read
              </button>
            )
          }
        >
          <ul className="divide-y divide-[#15233B]/8">
            {data.mentions.length === 0 && (
              <li className="px-5 py-6 text-xs italic text-[#15233B]/40">
                Nothing yet. New website applications land here, and teammates can tag you with @firstname in notes.
              </li>
            )}
            {data.mentions.map((m) => (
              <li
                key={m.id}
                onClick={() => openCard(m.card_id)}
                className={`px-5 py-3 text-sm cursor-pointer hover:bg-[#F6F3EC]/60 ${
                  !m.read ? "bg-[#EDE9FE]/50" : ""
                }`}
                data-testid={`mention-row-${m.id}`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{
                      backgroundColor: m.read ? "#cbd5e1" : m.source === "application" ? "#16A34A" : "#4F46E5",
                    }}
                  />
                  {m.source === "application" ? (
                    <div className="font-bold text-[#15233B]">
                      <span className="text-[#16A34A]">New website application</span>{" "}
                      <span className="font-normal text-[#15233B]/60">from</span>{" "}
                      {m.card_full_name}{" "}
                      <span className="font-mono text-xs text-[#15233B]/60">
                        {m.card_av_id}
                      </span>
                    </div>
                  ) : (
                    <div className="font-bold text-[#15233B]">
                      {m.by_user_name}{" "}
                      <span className="font-normal text-[#15233B]/60">
                        mentioned you on
                      </span>{" "}
                      {m.card_full_name}{" "}
                      <span className="font-mono text-xs text-[#15233B]/60">
                        {m.card_av_id}
                      </span>
                    </div>
                  )}
                  <span className="ml-auto text-[10px] text-[#15233B]/50">
                    {new Date(m.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="mt-1 text-xs text-[#15233B]/70 italic line-clamp-2 pl-4">
                  {m.source === "application" ? m.text : `“${m.text}”`}
                </div>
              </li>
            ))}
          </ul>
        </Section>

        <Section
          title="Snoozed leads"
          icon={Moon}
          pastel={PASTEL.royal}
          count={data.counts.snoozed || 0}
          testId="inbox-snoozed"
        >
          <ul className="divide-y divide-[#15233B]/8">
            {(!data.snoozed || data.snoozed.length === 0) && (
              <li className="px-5 py-6 text-xs italic text-[#15233B]/40">
                No snoozed leads — snooze one from its card to pause it from your daily lists.
              </li>
            )}
            {(data.snoozed || []).map((s) => (
              <Row
                key={s.id}
                card_full_name={s.full_name}
                card_av_id={s.av_id}
                card_id={s.id}
                card_stage={s.current_stage}
                stage_name={tplByStage[s.current_stage]?.name || ""}
              >
                <div className="text-right text-xs max-w-[240px]" data-testid={`snoozed-row-${s.id}`}>
                  <div className="font-bold text-[#283593]">wakes {s.snoozed_until}</div>
                  {s.snooze_reason && <div className="italic text-[#15233B]/60 truncate">{s.snooze_reason}</div>}
                  {s.snoozed_by_name && <div className="text-[#15233B]/45">by {s.snoozed_by_name}</div>}
                </div>
              </Row>
            ))}
          </ul>
        </Section>
      </div>

      {selected && (
        <CardDetailPanel
          card={selected}
          template={tplByStage[selected.current_stage]}
          templates={templates}
          users={users}
          lists={lists}
          onClose={() => setSelected(null)}
          onChanged={(u) => setSelected(u)}
        />
      )}
      <BrandFooter />
    </div>
  );
}
